import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal";
import Modal from "../components/Modal";
import StatusBadge from "../components/StatusBadge";
import { capitalizeFirstLetter } from "../../utils/letters";
import { listCourseTemplates } from "../api/courses";
import {
	createRecordedClass,
	deleteRecordedClass,
	listRecordedClasses,
	updateRecordedClass,
} from "../api/recordedClasses";
import { formatDate } from "../../utils/date";

const ROWS_PER_PAGE_DEFAULT = 10;
const ALLOWED_ROWS = [10, 25, 50, 100];

const SORT_OPTIONS = [
	{ value: "code", label: "Código" },
	{ value: "name", label: "Nombre" },
	{ value: "recordings", label: "Grabaciones" },
	{ value: "default_run__status", label: "Estado" },
];

const ALLOWED_SORTS = new Set(SORT_OPTIONS.map((option) => option.value));
const ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);

const emptyRecordingForm = {
	title: "",
	recording_url: "",
	session_date: "",
	notes: "",
	is_published: true,
};

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!ALLOWED_ROWS.includes(parsed)) return ROWS_PER_PAGE_DEFAULT;
	return parsed;
}

function parseSort(rawValue) {
	if (!ALLOWED_SORTS.has(rawValue)) return "code";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!ALLOWED_DIRECTIONS.has(rawValue)) return "asc";
	return rawValue;
}

function getFirstApiMessage(value) {
	if (Array.isArray(value)) return value[0] ?? "";
	if (typeof value === "string") return value;
	return "";
}

function getApiFieldErrors(data) {
	const next = {};
	if (!data || typeof data !== "object") return next;

	for (const key of [
		"course_run_id",
		"title",
		"recording_url",
		"session_date",
		"notes",
		"is_published",
	]) {
		const message = getFirstApiMessage(data[key]);
		if (message) next[key] = message;
	}

	if (typeof data.detail === "string") {
		next.general = data.detail;
	}

	return next;
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

function getCourseLabel(course) {
	return `${course.code || ""} - ${course.name || "Sin nombre"}`.trim();
}

const runStatusMap = {
	open: "Abierto",
	finished: "Finalizado",
	closed: "Cerrado",
};

const runStatusClassMap = {
	open: "status-open status",
	finished: "status-ok status",
	closed: "status-none status",
};

function getCourseRunStatus(course) {
	return (course?.default_run?.status || "").toLowerCase();
}

function canListRecordedClassCourse(course) {
	const status = getCourseRunStatus(course);
	return Boolean(
		course?.is_active &&
			course?.default_run?.id &&
			["open", "finished", "closed"].includes(status),
	);
}

function canEditRecordedClasses(course) {
	return Boolean(course?.is_active && getCourseRunStatus(course) === "open");
}

function getCourseRunStatusLabel(course) {
	const status = getCourseRunStatus(course);
	return runStatusMap[status] || course?.default_run?.status || "Sin edición";
}

function getCourseRunSummary(course) {
	const run = course.default_run;
	if (!run) return "Sin edición activa";

	const datePart =
		run.start_date && run.end_date
			? `${formatDate(run.start_date)} - ${formatDate(run.end_date)}`
			: "Sin fechas";
	return datePart;
}

function buildGroupMap(recordings) {
	return recordings.reduce((acc, recording) => {
		const key = Number(recording.course_template_id || 0);
		if (!acc[key]) acc[key] = [];
		acc[key].push(recording);
		return acc;
	}, {});
}

function sortRecordings(recordings) {
	return [...recordings].sort((left, right) => {
		const leftDate = new Date(left.session_date || 0).getTime();
		const rightDate = new Date(right.session_date || 0).getTime();
		if (leftDate !== rightDate) return rightDate - leftDate;
		return (left.title || "").localeCompare(right.title || "", "es");
	});
}

function getTodayISODate() {
	return new Date().toISOString().slice(0, 10);
}

export default function RecordedClassesList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [courses, setCourses] = useState([]);
	const [recordings, setRecordings] = useState([]);
	const [coursesLoading, setCoursesLoading] = useState(false);
	const [recordingsLoading, setRecordingsLoading] = useState(false);
	const [alert, setAlert] = useState(null);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [detailOpen, setDetailOpen] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const [modifyOpen, setModifyOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [selectedCourse, setSelectedCourse] = useState(null);
	const [selectedRecordingId, setSelectedRecordingId] = useState("");
	const [recordingToDelete, setRecordingToDelete] = useState(null);
	const [form, setForm] = useState(emptyRecordingForm);
	const [fieldErrors, setFieldErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);

	async function refreshCourses() {
		setCoursesLoading(true);
		try {
			const data = await listCourseTemplates({});
			const list = Array.isArray(data) ? data : (data.results ?? []);
			setCourses(list.filter(canListRecordedClassCourse));
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudieron cargar los cursos activos.",
			});
		} finally {
			setCoursesLoading(false);
		}
	}

	async function refreshRecordings() {
		setRecordingsLoading(true);
		try {
			const data = await listRecordedClasses({});
			setRecordings(Array.isArray(data) ? data : (data.results ?? []));
		} catch {
			setRecordings([]);
			setAlert({
				type: "danger",
				msg: "No se pudieron cargar las grabaciones.",
			});
		} finally {
			setRecordingsLoading(false);
		}
	}

	useEffect(() => {
		refreshCourses();
		refreshRecordings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const groupedRecordings = useMemo(
		() => buildGroupMap(recordings),
		[recordings],
	);

	const activeCourses = useMemo(() => {
		const query = search.trim().toLowerCase();
		const sorted = [...courses];

		sorted.sort((left, right) => {
			if (sortField === "recordings") {
				const leftValue = (groupedRecordings[left.id] || []).length;
				const rightValue = (groupedRecordings[right.id] || []).length;
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			if (sortField === "default_run__status") {
				const leftValue = (left.default_run?.status || "").toLowerCase();
				const rightValue = (right.default_run?.status || "").toLowerCase();
				if (leftValue === rightValue) return 0;
				if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
				return leftValue < rightValue ? 1 : -1;
			}

			const leftValue = (left[sortField] ?? "").toString().toLowerCase();
			const rightValue = (right[sortField] ?? "").toString().toLowerCase();
			if (leftValue === rightValue) return 0;
			if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
			return leftValue < rightValue ? 1 : -1;
		});

		if (!query) return sorted;

		return sorted.filter((course) => {
			const count = (groupedRecordings[course.id] || []).length.toString();
			return (
				(course.code || "").toLowerCase().includes(query) ||
				(course.name || "").toLowerCase().includes(query) ||
				(course.description || "").toLowerCase().includes(query) ||
				(course.default_run?.status || "").toLowerCase().includes(query) ||
				count.includes(query)
			);
		});
	}, [courses, groupedRecordings, search, sortDirection, sortField]);

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
	}, [searchParams]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection]);

	const totalRows = activeCourses.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "code") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "asc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		currentPage,
		rowsPerPage,
		search,
		searchParams,
		setSearchParams,
		sortDirection,
		sortField,
	]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("code");
		setSortDirection("asc");
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	function getRecordingsForCourse(course) {
		return sortRecordings(groupedRecordings[course.id] || []);
	}

	function openCourseAction(course, action) {
		setAlert(null);
		setSelectedCourse(course);
		setSelectedRecordingId("");
		setRecordingToDelete(null);
		setFieldErrors({});
		setForm({
			...emptyRecordingForm,
			session_date: getTodayISODate(),
		});

		if (!course?.default_run?.id && action !== "detail") {
			setAlert({
				type: "warning",
				msg: "Este curso no tiene una edición activa para administrar grabaciones.",
			});
			return;
		}

		if (["add", "modify", "delete"].includes(action) && !canEditRecordedClasses(course)) {
			setAlert({
				type: "warning",
				msg: "Solo puedes administrar enlaces cuando el curso está activo y su estado es Abierto.",
			});
			return;
		}

		if (action === "detail") setDetailOpen(true);
		if (action === "add") setAddOpen(true);
		if (action === "modify") setModifyOpen(true);
		if (action === "delete") setDeleteOpen(true);
	}

	function openDetail(course) {
		openCourseAction(course, "detail");
	}

	function openAdd(course) {
		openCourseAction(course, "add");
	}

	function openModify(course) {
		openCourseAction(course, "modify");
	}

	function openDelete(course) {
		openCourseAction(course, "delete");
	}

	function closeAllModals() {
		if (saving || deleteLoading) return;
		setDetailOpen(false);
		setAddOpen(false);
		setModifyOpen(false);
		setDeleteOpen(false);
		setSelectedCourse(null);
		setSelectedRecordingId("");
		setRecordingToDelete(null);
		setFieldErrors({});
	}

	function validateForm() {
		const next = {};
		if (!(form.title || "").trim()) next.title = "El título es obligatorio.";
		if (!(form.recording_url || "").trim()) {
			next.recording_url = "El enlace es obligatorio.";
		}
		if (!(form.session_date || "").trim()) {
			next.session_date = "La fecha es obligatoria.";
		}
		if (!selectedCourse?.default_run?.id) {
			next.general =
				"El curso no tiene una edición activa asociada para guardar grabaciones.";
		}
		if (!canEditRecordedClasses(selectedCourse)) {
			next.general =
				"Solo puedes guardar grabaciones cuando el curso está activo y su estado es Abierto.";
		}
		setFieldErrors(next);
		return Object.keys(next).length === 0;
	}

	async function refreshAndKeepCourse(courseId) {
		await refreshRecordings();
		if (!courseId) return;
		const freshCourse = courses.find((course) => course.id === courseId);
		if (freshCourse) setSelectedCourse(freshCourse);
	}

	async function submitRecording() {
		if (!validateForm()) return;

		setSaving(true);
		setAlert(null);
		try {
			const payload = {
				course_run_id: selectedCourse.default_run.id,
				title: form.title.trim(),
				recording_url: form.recording_url.trim(),
				session_date: form.session_date,
				notes: form.notes || "",
				is_published: Boolean(form.is_published),
			};

			if (addOpen) {
				await createRecordedClass(payload);
				setAlert({
					type: "success",
					msg: "Grabación agregada correctamente.",
				});
			} else if (modifyOpen) {
				if (!selectedRecordingId) {
					setFieldErrors({
						general: "Selecciona una clase para modificar.",
					});
					return;
				}
				await updateRecordedClass(selectedRecordingId, payload);
				setAlert({
					type: "success",
					msg: "Grabación actualizada correctamente.",
				});
			}

			await refreshAndKeepCourse(selectedCourse.id);
			setAddOpen(false);
			setModifyOpen(false);
			setSelectedRecordingId("");
			setForm({
				...emptyRecordingForm,
				session_date: getTodayISODate(),
			});
			setFieldErrors({});
		} catch (error) {
			const apiData = error.response?.data || {};
			const apiFieldErrors = getApiFieldErrors(apiData);
			setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
			setAlert({
				type: "danger",
				msg:
					typeof apiData.detail === "string"
						? apiData.detail
						: apiFieldErrors.general ||
							"No se pudo guardar la grabación.",
			});
		} finally {
			setSaving(false);
		}
	}

	function handleSelectRecording(recordingId) {
		setSelectedRecordingId(recordingId);
		const recording = getRecordingsForCourse(selectedCourse).find(
			(item) => String(item.id) === String(recordingId),
		);
		if (!recording) {
			setForm({
				...emptyRecordingForm,
				session_date: getTodayISODate(),
			});
			return;
		}

		setForm({
			title: recording.title || "",
			recording_url: recording.recording_url || "",
			session_date: recording.session_date || "",
			notes: recording.notes || "",
			is_published: Boolean(recording.is_published),
		});
		setFieldErrors({});
	}

	function askDelete(recording) {
		setRecordingToDelete(recording);
	}

	async function confirmDelete() {
		if (!recordingToDelete) return;
		setDeleteLoading(true);
		setAlert(null);
		try {
			await deleteRecordedClass(recordingToDelete.id);
			await refreshAndKeepCourse(selectedCourse?.id);
			setRecordingToDelete(null);
			setAlert({
				type: "success",
				msg: "Grabación eliminada correctamente.",
			});
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo eliminar la grabación.",
			});
		} finally {
			setDeleteLoading(false);
		}
	}

	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const paginatedCourses = activeCourses.slice(
		startIndex,
		startIndex + rowsPerPage,
	);

	const selectedCourseRecordings = selectedCourse
		? getRecordingsForCourse(selectedCourse)
		: [];

	const hasSearchQuery = search.trim().length > 0;
	const emptyMessage = hasSearchQuery
		? "No hay cursos que coincidan con la búsqueda."
		: courses.length === 0
			? "No hay cursos activos registrados."
			: "No hay resultados para los filtros actuales.";

	let rows;
	if (coursesLoading) {
		rows = (
			<tr>
				<td colSpan="5" className="text-center py-4 text-muted">
					Cargando cursos activos...
				</td>
			</tr>
		);
	} else if (paginatedCourses.length === 0) {
		rows = (
			<tr>
				<td colSpan="5" className="text-center py-4 text-muted">
					{emptyMessage}
				</td>
			</tr>
		);
	} else {
		rows = paginatedCourses.map((course) => {
			const recordingsCount = (groupedRecordings[course.id] || []).length;
			const canSeeDetail = Boolean(course.default_run?.id);
			const canEditLinks = canEditRecordedClasses(course);
			const status = getCourseRunStatus(course);
			const statusLabel = getCourseRunStatusLabel(course);
			const statusClassName = runStatusClassMap[status] || "status-chip";

			return (
				<tr key={course.id}>
					<td className="fw-semibold">
						<span>{highlightSearchMatch(course.code, search)}</span>
					</td>
					<td>
						<div className="d-flex flex-column">
							<span>{highlightSearchMatch(course.name, search)}</span>
							<span className="text-muted small">
								{getCourseRunSummary(course)}
							</span>
						</div>
					</td>
					<td>
						<span>{recordingsCount}</span>
					</td>
					<td>
						<span className={statusClassName} title={`Estado del curso: ${statusLabel}`}>
							{statusLabel}
						</span>
					</td>
					<td className="text-center">
						<div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">
							<button
								type="button"
								className="btn-sm adm-see-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
								onClick={() => openDetail(course)}
								title="Detalle"
								aria-label="Detalle"
								disabled={!canSeeDetail}
							>
								<i className="ri-eye-fill" />
							</button>
							<button
								type="button"
								className="btn-sm adm-create-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
								onClick={() => openAdd(course)}
								title={
									canEditLinks
										? "Agregar"
										: "Solo disponible para cursos activos y abiertos"
								}
								aria-label="Agregar"
								disabled={!canEditLinks}
							>
								<i className="ri-add-fill" />
							</button>
							<button
								type="button"
								className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
								onClick={() => openModify(course)}
								title={
									canEditLinks
										? "Modificar"
										: "Solo disponible para cursos activos y abiertos"
								}
								aria-label="Modificar"
								disabled={!canEditLinks || recordingsCount === 0}
							>
								<i className="ri-edit-fill" />
							</button>
							<button
								type="button"
								className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
								onClick={() => openDelete(course)}
								title={
									canEditLinks
										? "Eliminar"
										: "Solo disponible para cursos activos y abiertos"
								}
								aria-label="Eliminar"
								disabled={!canEditLinks || recordingsCount === 0}
							>
								<i className="ri-delete-bin-line" />
							</button>
						</div>
					</td>
				</tr>
			);
		});
	}

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div>
					<h2 className="m-0">Clases grabadas</h2>
				</div>
			</div>

			{alert && (
				<div className={`alert alert-${alert.type} mt-3 mb-0`} role="alert">
					<span>{alert.msg}</span>
				</div>
			)}

			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={currentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) => Math.max(1, prev - 1))
								}
								disabled={currentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={currentPage}
								onChange={(event) =>
									setCurrentPage(Number(event.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
									(page) => (
										<option key={page} value={page}>
											{page}
										</option>
									),
								)}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(totalPages, prev + 1),
									)
								}
								disabled={currentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={currentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Buscar por código, nombre, estado o grabaciones"
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(event) =>
										setRowsPerPage(Number(event.target.value))
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(event) =>
										setSortField(event.target.value)
									}
								>
									{SORT_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th>
										<span>Código</span>
									</th>
									<th>
										<span>Curso</span>
									</th>
									<th>
										<span>Grabaciones</span>
									</th>
									<th>
										<span>Estado</span>
									</th>
									<th className="text-center" style={{ width: 260 }}>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>{rows}</tbody>
						</table>
					</div>

					<div className="table-footer-summary">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} - {endIndex} de{" "}
							{totalRows} registros
						</span>
					</div>
				</div>
			</div>

			<Modal
                title={`Detalle de ${getCourseLabel(selectedCourse || {})}`}
                open={detailOpen}
                onClose={closeAllModals}
                dialogClassName="modal-dialog modal-lg modal-dialog-scrollable"
                footer={
                    <button className="adm-del-btn" onClick={closeAllModals}>
                        <span>Cerrar</span>
                    </button>
                }
            >
                {selectedCourse ? (
                    <div className="font-n">
                        <section className="card  border-0">
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between border-bottom flex-wrap gap-2 mb-4 pb-2">
                                    <h5 className="mb-0 fw-bold">Información General</h5>
                                </div>
                                <div className="container-fluid p-0 mb-4">
                                    <div className="row g-3">
                                        <div className="col-md-6 pb-2 d-flex flex-wrap justify-content-start">
                                            <label className="mb-1 me-2 fw-semibold"><span>Código:</span></label>
                                            <span>{selectedCourse.code || "—"}</span>
                                        </div>
                                        <div className="col-md-6 pb-2 d-flex flex-wrap justify-content-start">
                                            <label className="mb-1 me-2 fw-semibold"><span>Nombre:</span></label>
                                            <span>{capitalizeFirstLetter(selectedCourse.name) || "—"}</span>
                                        </div>
                                        <div className="col-md-6 pb-2 d-flex flex-wrap justify-content-start">
                                            <label className="mb-1 me-2 fw-semibold"><span>Estado:</span></label>
                                            <span>{selectedCourse.default_run?.status ? (runStatusMap[selectedCourse.default_run.status.toLowerCase()] || selectedCourse.default_run.status) : "Sin edición"}</span>
                                        </div>
                                        <div className="col-md-6 pb-2 d-flex flex-wrap justify-content-start">
                                            <label className="mb-1 me-2 fw-semibold"><span>Fechas:</span></label>
                                            <span>
                                                {selectedCourse.default_run?.start_date &&
                                                selectedCourse.default_run?.end_date
                                                    ? `${formatDate(selectedCourse.default_run.start_date)} - ${formatDate(selectedCourse.default_run.end_date)}`
                                                    : "Sin fechas"}
                                            </span>
                                        </div>
                                        <div className="col-12 pb-2 d-flex flex-wrap justify-content-start">
                                            <label className="mb-1 me-2 fw-semibold"><span>Grabaciones registradas:</span></label>
                                            <span>{selectedCourseRecordings.length}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center justify-content-between border-bottom flex-wrap gap-2 mb-4 pb-2">
                                    <h5 className="mb-0 fw-bold">Enlaces registrados</h5>
                                </div>

                                <div className="container-fluid p-0">
                                    {recordingsLoading ? (
                                        <p className="text-muted mb-0">Cargando grabaciones...</p>
                                    ) : selectedCourseRecordings.length === 0 ? (
                                        <p className="text-muted mb-0">
                                            No hay enlaces registrados para este curso.
                                        </p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th><span>Título y Fecha</span></th>
                                                        <th><span>Notas</span></th>
                                                        <th className="text-center"><span>Estado</span></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedCourseRecordings.map((recording) => (
                                                        <tr key={recording.id}>
                                                            <td>
                                                                <div className="fw-semibold" style={{ color: "var(--dark-blue)" }}>
                                                                    <span>{capitalizeFirstLetter(recording.title) || "Sin título"}</span>
                                                                </div>
                                                                <div className="text-muted fs-6">
                                                                    <span>{formatDate(recording.session_date) ||
                                                                        recording.session_date}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small text-muted">
                                                                    <span>
                                                                        {recording.notes
                                                                            ? capitalizeFirstLetter(recording.notes)
                                                                            : "Sin notas registradas."}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <StatusBadge
                                                                    active={Boolean(recording.is_published)}
                                                                    trueLabel="Visible"
                                                                    falseLabel="Oculto"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </section>
                    </div>
                ) : null}
            </Modal>

			<Modal
				title={`Agregar grabación - ${getCourseLabel(selectedCourse || {})}`}
				open={addOpen}
				onClose={closeAllModals}
				dialogClassName="modal-dialog modal-lg modal-dialog-scrollable"
				footer={
					<>
						<button
							type="button"
							className="btn adm-del-btn"
							onClick={closeAllModals}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							type="button"
							className="btn adm-save-btn"
							onClick={submitRecording}
							disabled={saving}
						>
							<span>{saving ? "Guardando..." : "Guardar"}</span>
						</button>
					</>
				}
			>
				{selectedCourse ? (
					<div className="d-flex flex-column gap-3">
						{fieldErrors.general && (
							<div className="alert alert-danger mb-0" role="alert">
								{fieldErrors.general}
							</div>
						)}
						<form
							className="row g-3"
							onSubmit={(event) => {
								event.preventDefault();
								submitRecording();
							}}
						>
							<div className="col-12">
								<label className="form-label">
									<span>Título</span> <span className="text-danger">*</span>
								</label>
								<input
									className={`form-control ${fieldErrors.title ? "is-invalid" : ""}`}
									value={form.title}
									onChange={(event) =>
										setForm((prev) => ({ ...prev, title: event.target.value }))
									}
								/>
								{fieldErrors.title && (
									<div className="invalid-feedback">{fieldErrors.title}</div>
								)}
							</div>
							<div className="col-12">
								<label className="form-label">
									<span>Enlace de grabación</span>{" "}
									<span className="text-danger">*</span>
								</label>
								<input
									className={`form-control ${fieldErrors.recording_url ? "is-invalid" : ""}`}
									value={form.recording_url}
									onChange={(event) =>
										setForm((prev) => ({
											...prev,
											recording_url: event.target.value,
										}))
									}
									placeholder="https://teams.microsoft.com/..."
								/>
								{fieldErrors.recording_url && (
									<div className="invalid-feedback">
										{fieldErrors.recording_url}
									</div>
								)}
							</div>
							<div className="col-md-6">
								<label className="form-label">
									<span>Fecha de clase</span>{" "}
									<span className="text-danger">*</span>
								</label>
								<input
									type="date"
									className={`form-control ${fieldErrors.session_date ? "is-invalid" : ""}`}
									value={form.session_date}
									onChange={(event) =>
										setForm((prev) => ({
											...prev,
											session_date: event.target.value,
										}))
									}
								/>
								{fieldErrors.session_date && (
									<div className="invalid-feedback d-block">
										{fieldErrors.session_date}
									</div>
								)}
							</div>
							<div className="col-md-6">
								<label className="form-label"><span>Estado</span></label>
								<div className="form-check pt-2">
									<input
										className="form-check-input"
										type="checkbox"
										checked={form.is_published}
										onChange={(event) =>
											setForm((prev) => ({
												...prev,
												is_published: event.target.checked,
											}))
										}
										id="recordingAddPublished"
									/>
									<label
										className="form-check-label"
										htmlFor="recordingAddPublished"
									>
										<span>Visible para el curso</span>
									</label>
								</div>
							</div>
							<div className="col-12">
								<label className="form-label"><span>Notas</span></label>
								<textarea
									className="form-control"
									rows={3}
									value={form.notes}
									onChange={(event) =>
										setForm((prev) => ({ ...prev, notes: event.target.value }))
									}
								/>
							</div>
						</form>
					</div>
				) : (
					<p className="text-muted mb-0">Selecciona un curso.</p>
				)}
			</Modal>

			<Modal
				title={`Modificar grabación - ${getCourseLabel(selectedCourse || {})}`}
				open={modifyOpen}
				onClose={closeAllModals}
				dialogClassName="modal-dialog modal-lg modal-dialog-scrollable"
				footer={
					<>
						<button
							type="button"
							className="btn adm-del-btn"
							onClick={closeAllModals}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							type="button"
							className="btn adm-upd-btn recording-save-btn"
							onClick={submitRecording}
							disabled={saving || !selectedRecordingId}
						>
							<span>{saving ? "Guardando..." : "Guardar cambios"}</span>
						</button>
					</>
				}
			>
				{selectedCourse ? (
					<div className="d-flex flex-column gap-3">
						{fieldErrors.general && (
							<div className="alert alert-danger mb-0" role="alert">
								{fieldErrors.general}
							</div>
						)}
						<div>
							<label className="form-label">
								<span>Clase a modificar</span>{" "}
								<span className="text-danger">*</span>
							</label>
							<select
								className="form-select"
								value={selectedRecordingId}
								onChange={(event) => handleSelectRecording(event.target.value)}
							>
								<option value="">Selecciona una clase</option>
								{selectedCourseRecordings.map((recording) => (
									<option key={recording.id} value={recording.id}>
										{formatDate(recording.session_date) || recording.session_date} -{" "}
										{recording.title || "Sin título"}
									</option>
								))}
							</select>
						</div>

						<form
							className="row g-3"
							onSubmit={(event) => {
								event.preventDefault();
								submitRecording();
							}}
						>
							<div className="col-12">
								<label className="form-label">
									<span>Título</span> <span className="text-danger">*</span>
								</label>
								<input
									className={`form-control ${fieldErrors.title ? "is-invalid" : ""}`}
									value={form.title}
									onChange={(event) =>
										setForm((prev) => ({ ...prev, title: event.target.value }))
									}
									disabled={!selectedRecordingId}
								/>
								{fieldErrors.title && (
									<div className="invalid-feedback">{fieldErrors.title}</div>
								)}
							</div>
							<div className="col-12">
								<label className="form-label">
									<span>Enlace de grabación</span>{" "}
									<span className="text-danger">*</span>
								</label>
								<input
									className={`form-control ${fieldErrors.recording_url ? "is-invalid" : ""}`}
									value={form.recording_url}
									onChange={(event) =>
										setForm((prev) => ({
											...prev,
											recording_url: event.target.value,
										}))
									}
									placeholder="https://teams.microsoft.com/..."
									disabled={!selectedRecordingId}
								/>
								{fieldErrors.recording_url && (
									<div className="invalid-feedback">
										{fieldErrors.recording_url}
									</div>
								)}
							</div>
							<div className="col-md-6">
								<label className="form-label">
									<span>Fecha de clase</span>{" "}
									<span className="text-danger">*</span>
								</label>
								<input
									type="date"
									className={`form-control ${fieldErrors.session_date ? "is-invalid" : ""}`}
									value={form.session_date}
									onChange={(event) =>
										setForm((prev) => ({
											...prev,
											session_date: event.target.value,
										}))
									}
									disabled={!selectedRecordingId}
								/>
								{fieldErrors.session_date && (
									<div className="invalid-feedback d-block">
										{fieldErrors.session_date}
									</div>
								)}
							</div>
							<div className="col-md-6">
								<label className="form-label"><span>Estado</span></label>
								<div className="form-check pt-2">
									<input
										className="form-check-input"
										type="checkbox"
										checked={form.is_published}
										onChange={(event) =>
											setForm((prev) => ({
												...prev,
												is_published: event.target.checked,
											}))
										}
										disabled={!selectedRecordingId}
										id="recordingEditPublished"
									/>
									<label
										className="form-check-label"
										htmlFor="recordingEditPublished"
									>
										<span>Visible para el curso</span>
									</label>
								</div>
							</div>
							<div className="col-12">
								<label className="form-label"><span>Notas</span></label>
								<textarea
									className="form-control"
									rows={3}
									value={form.notes}
									onChange={(event) =>
										setForm((prev) => ({ ...prev, notes: event.target.value }))
									}
									disabled={!selectedRecordingId}
								/>
							</div>
						</form>
					</div>
				) : (
					<p className="text-muted mb-0">Selecciona un curso.</p>
				)}
			</Modal>

			<Modal
                title={`Eliminar grabaciones - ${getCourseLabel(selectedCourse || {})}`}
                open={deleteOpen}
                onClose={closeAllModals}
                dialogClassName="modal-dialog modal-lg modal-dialog-scrollable"
                footer={
                    <button type="button" className="btn adm-del-btn" onClick={closeAllModals}>
                        <span>Cerrar</span>
                    </button>
                }
            >
                {selectedCourse ? (
                    <div className="font-n">
                        <section className="card border-0">
                            <div className="card-body ">
                                
                                <div className="d-flex align-items-center justify-content-between border-bottom flex-wrap gap-2 mb-4 pb-2">
                                    <h5 className="mb-0 fw-bold"><span>Selecciona la grabación a eliminar</span></h5>
                                </div>

                                <div className="container-fluid p-0">
                                    {recordingsLoading ? (
                                        <p className="text-muted mb-0"><span>Cargando grabaciones...</span></p>
                                    ) : selectedCourseRecordings.length === 0 ? (
                                        <p className="text-muted mb-0">
                                            <span>No hay grabaciones para eliminar en este curso.</span>
                                        </p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th><span>Título y Fecha</span></th>
                                                        <th className="text-center"><span>Acción</span></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedCourseRecordings.map((recording) => (
                                                        <tr key={recording.id}>
                                                            <td>
                                                                <div className="fw-semibold" style={{ color: "var(--dark-blue)" }}>
                                                                    <span>{capitalizeFirstLetter(recording.title) || "Sin título"}</span>
                                                                </div>
                                                                <div className="text-muted fs-6">
                                                                    <span>
                                                                        {formatDate(recording.session_date) ||
                                                                            recording.session_date}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn-sm adm-del-btn border-0 py-1 px-2"
                                                                    onClick={() => askDelete(recording)}
                                                                    title="Eliminar"
                                                                    aria-label="Eliminar"
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                
                            </div>
                        </section>
                    </div>
                ) : null}
            </Modal>

			<ConfirmModal
				open={!!recordingToDelete}
				onClose={() => setRecordingToDelete(null)}
				onConfirm={confirmDelete}
				title="Confirmar eliminación"
				message={`Se eliminará la grabación ${recordingToDelete?.title || "seleccionada"}.`}
				confirmLabel={deleteLoading ? "Eliminando..." : "Eliminar"}
				loading={deleteLoading}
			/>
		</div>
	);
}
